/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// probe-inspector.js — the connectivity probe (Feature 70): arm it, and a
// hover highlights the net under the cursor while a click PINS one so it
// survives edits/switch flips. Owns its own netlist cache, the net-highlight
// overlay, and the net-status readout; borrows the shared hover ring and asks
// the host to resolve world geometry + coordinate with the other tools.
//
// It renders from the netlist and the live SimOverlay (for level tints) — never
// the engine directly. Pulled out of DeskController so "what net is this, and
// draw it" lives in one place instead of threaded through the controller.
//
// THE NET IT SHOWS IS THE WIRED ONE — board nodes and wires, every switch and
// button an open contact (`bridges: false`, the schematic's partition). The
// probe is also where a net is NAMED, and a name names what the build
// connected: through the conducting netlist a closed switch carried the
// highlight, and the name, from its output onto the rail and every pin on it.
// The LEVEL it tints with is still the live one — read from the conducting
// netlist the engine solves, at the probed point.

import { formatNumber, t } from "../i18n.js";
import { el } from "../dom.js";
import { PopupManager } from "../popup-manager.js";
import { PX_PER_UNIT } from "../desk/desk-geometry.js";
import { summarizeNet } from "../sim/netlist.js";
import { RESERVED_NET_NAMES } from "../model/desk-doc.js";
import { NetlistCache } from "./netlist-cache.js";
import { NetHighlight } from "./net-highlight.js";

/**
 * A current as the probe says it: µA under a milliamp, mA under an amp, A
 * beyond; three significant figures. Null for none known.
 * @param {number|null|undefined} amps
 */
export function currentText(amps) {
  if (amps == null || !Number.isFinite(amps)) return null;
  const figures = { maximumSignificantDigits: 3 };
  if (amps >= 1) return t("probe.amps", { amps: formatNumber(amps, figures) });
  if (amps >= 1e-3) {
    return t("probe.milliamps", { amps: formatNumber(amps * 1e3, figures) });
  }
  return t("probe.microamps", {
    amps: formatNumber(Math.round(amps * 1e6 * 10) / 10, figures),
  });
}

/** Radius of the shared hover ring (pitch units — a shade over one hole);
    keep 2× this in step with `.hole-ring`'s diameter in app.css. See
    desk-controller.js. */
const RING_RADIUS = 0.55;

export class ProbeInspector {
  #doc;
  #viewport;
  #ring; // the shared hover-ring element (owned by the controller)
  #simOverlay;
  #hitTest; // (world) → { address, x, y } | null
  #addressWorld; // (address) → { x, y } | null
  #onStateChange; // ({ armed }) → void
  #onNameNet; // (address, name, staleAddresses) → void
  #onClearNetNames; // (addresses) → void
  #onAddToScope; // (address) → void — pin the probed net as an analyzer channel
  #onSetScopeGround; // (address|null) → void — the analyzer's ground lead
  #coord; // { cancelPlacement, disarmWireTool, deselect, hideHover }

  #netlist; // the WIRING partition: what is shown, named and broadcast
  #liveNetlist; // the conducting one the engine solves: where levels live
  #highlight;
  #netStatus;

  #armed = false;
  #anchor = null; // pinned point address, or null
  #hoverWire = null; // wire id under the cursor, or null (see onWireHover)
  // The point the unpinned readout shows (a hole, or a hovered wire's `from`),
  // kept so each sim-state redraws it (`refresh`), or null.
  #hoverAt = null;
  #wirePathOf; // wire id → the path it is drawn along (WireLayer.restingPath)

  constructor({
    doc,
    overlay,
    viewport,
    ring,
    simOverlay,
    hitTest,
    addressWorld,
    wirePathOf,
    onStateChange,
    onNameNet,
    onClearNetNames,
    onAddToScope,
    onSetScopeGround,
    coordinate,
    netlist,
    liveNetlist,
  }) {
    this.#doc = doc;
    this.#viewport = viewport;
    this.#ring = ring;
    this.#simOverlay = simOverlay;
    this.#hitTest = hitTest;
    this.#addressWorld = addressWorld;
    this.#wirePathOf = wirePathOf ?? null;
    this.#onStateChange = onStateChange;
    this.#onNameNet = onNameNet;
    this.#onClearNetNames = onClearNetNames;
    this.#onAddToScope = onAddToScope;
    this.#onSetScopeGround = onSetScopeGround ?? null;
    this.#coord = coordinate;

    this.#netlist = netlist ?? new NetlistCache(doc, { bridges: false });
    this.#liveNetlist = liveNetlist ?? new NetlistCache(doc);
    this.#highlight = new NetHighlight(overlay);
    this.#netStatus = el("div", { class: "net-status", hidden: true });
    viewport.append(this.#netStatus);
  }

  get armed() {
    return this.#armed;
  }

  /** Arm probe mode: hover highlights a net, click pins it. */
  arm() {
    if (this.#armed) return;
    this.#coord.cancelPlacement();
    this.#coord.disarmWireTool();
    this.#coord.disarmBusTool?.();
    this.#coord.disarmCutTool?.();
    this.#coord.deselect();
    this.#coord.hideHover();
    this.#armed = true;
    this.#anchor = null;
    this.#viewport.classList.add("desk-viewport--probing");
    this.#onStateChange?.({ armed: true });
  }

  disarm() {
    if (!this.#armed) return;
    this.#armed = false;
    this.#anchor = null;
    this.#hoverWire = null;
    this.#hoverAt = null;
    this.#viewport.classList.remove("desk-viewport--probing");
    this.#highlight.clear();
    this.#ring.hidden = true;
    this.#ring.classList.remove("hole-ring--illegal");
    this.#netStatus.hidden = true;
    this.#emitProbed(null);
    this.#onStateChange?.({ armed: false });
  }

  toggle() {
    if (this.#armed) this.disarm();
    else this.arm();
  }

  /** Escape while armed: first unpin, then disarm. True when it consumed it. */
  handleEscape() {
    if (!this.#armed) return false;
    if (this.#anchor) {
      this.#anchor = null;
      this.#hoverAt = null;
      this.#highlight.clear();
      this.#netStatus.hidden = true;
      this.#emitProbed(null);
    } else {
      this.disarm();
    }
    return true;
  }

  /**
   * Rebuild the readout and highlight from what it shows — the pinned anchor,
   * else the point the cursor rests on — after an edit, a switch flip or a
   * sim tick. Redrawn only as the pointer moved, a resting hover kept the
   * level and volts it arrived to (the fourth regrade, 2026-10-22).
   */
  refresh() {
    const at = this.#anchor ?? this.#hoverAt;
    if (!at) return;
    // The point may vanish (its board/PSU deleted) — drop it then.
    if (!this.#netlist.netOf(at)) {
      this.#anchor = null;
      this.#hoverAt = null;
      this.#highlight.clear();
      this.#netStatus.hidden = true;
      this.#emitProbed(null);
      return;
    }
    this.#showNetFor(at, Boolean(this.#anchor));
  }

  /** Probe pointermove: highlight the hovered point's net (unless pinned). */
  trackMove(world) {
    if (this.#anchor) return; // pinned — hover doesn't disturb it
    const hit = this.#hitTest(world);
    this.#hoverAt = hit?.address ?? null;
    if (!hit?.address) {
      this.#ring.hidden = true;
      this.#highlight.clear();
      this.#netStatus.hidden = true;
      this.#emitProbed(null);
      return;
    }
    const r = RING_RADIUS * PX_PER_UNIT;
    this.#ring.style.left = `${hit.x * PX_PER_UNIT - r}px`;
    this.#ring.style.top = `${hit.y * PX_PER_UNIT - r}px`;
    this.#ring.hidden = false;
    this.#showNetFor(hit.address, false);
  }

  /** Probe click: pin the hovered net, or unpin if already pinned. */
  commitClick(world) {
    if (this.#anchor) {
      this.#anchor = null; // re-click unpins
      this.trackMove(world); // fall back to hover highlight at the cursor
      return;
    }
    const hit = this.#hitTest(world);
    if (!hit?.address || !this.#netlist.netOf(hit.address)) return;
    this.#anchor = hit.address;
    this.#showNetFor(hit.address, true);
  }

  /** A wire hovered under the probe (via the wire hit stroke). */
  onWireHover(wireId) {
    if (!this.#armed) return;
    // Remembered even while pinned: a right-click over a wire's BODY has no
    // hole to hit-test, so the menu falls back to whatever wire is under the
    // cursor (#onContextMenu).
    this.#hoverWire = wireId ?? null;
    if (this.#anchor) return; // pinned — hover doesn't disturb it
    const wire = wireId ? this.#doc.getWire(wireId) : null;
    this.#hoverAt = wire?.from ?? null;
    if (!wire) {
      this.#highlight.clear();
      this.#netStatus.hidden = true;
      this.#emitProbed(null);
      return;
    }
    this.#ring.hidden = true;
    this.#showNetFor(wire.from, false);
  }

  /** Geometry the highlight overlay draws by (world px). */
  #highlightGeometry() {
    return {
      positionOf: (address) => {
        const w = this.#addressWorld(address);
        return w ? { x: w.x * PX_PER_UNIT, y: w.y * PX_PER_UNIT } : null;
      },
      wireEndpointsOf: (wireId) => {
        const wire = this.#doc.getWire(wireId);
        if (!wire) return null;
        const a = this.#addressWorld(wire.from);
        const b = this.#addressWorld(wire.to);
        if (!a || !b) return null;
        return {
          a: { x: a.x * PX_PER_UNIT, y: a.y * PX_PER_UNIT },
          b: { x: b.x * PX_PER_UNIT, y: b.y * PX_PER_UNIT },
        };
      },
      wirePathOf: (wireId) => this.#wirePathOf?.(wireId) ?? null,
    };
  }

  /**
   * Broadcast the probed net so OTHER views (the schematic, Feature 150) can
   * highlight the SAME net by its stable id. `null` clears the highlight
   * everywhere. One-way, like every `chiphippo:*` state push.
   */
  #emitProbed(netId, level = null, pinned = false) {
    window.dispatchEvent(
      new CustomEvent("chiphippo:net-probed", {
        detail: {
          netId: netId ?? null,
          level: netId ? level : null,
          pinned: netId ? pinned : false,
        },
      }),
    );
  }

  /** Show the net containing `address`, pinned or transient. */
  #showNetFor(address, pinned) {
    const netId = this.#netlist.netOf(address);
    const net = netId ? this.#netlist.netInfo(netId) : null;
    // While running, tint the highlight + lead the summary with the level —
    // the live net's, which a wired net is always wholly inside. Read at the
    // POINT where the overlay can (`levelAt`): the netlist the engine solved
    // may split what the shared one joins (Spice Lite's inductors).
    const level =
      this.#simOverlay.levelAt?.(address) ??
      this.#simOverlay.levelOfNet(this.#liveNetlist.netOf(address));
    this.#highlight.show(net, this.#highlightGeometry(), pinned, level);
    this.#emitProbed(netId, level, pinned);
    if (net) {
      // The readout leads with the user NAME (Feature 120), then the level
      // while running, then the connectivity summary.
      const name = this.#netlist.nameOf(netId);
      // Spice Lite knows the VOLTAGE of every net something holds (a rail,
      // an output, a resistive path to either, an RC node): said after the
      // level.
      const volts = this.#simOverlay.voltsAt
        ? this.#simOverlay.voltsAt(address)
        : this.#simOverlay.voltsOfNet?.(this.#liveNetlist.netOf(address));
      const voltage =
        volts == null
          ? null
          : t("probe.volts", {
              volts: formatNumber(volts, { maximumFractionDigits: 2 }),
            });
      // …and the current through the lead in the hole the probe is ON (an
      // LED's, a resistor's beside it, an output driving it): no part shows
      // a current of its own, so this is where it is read.
      const current = currentText(this.#simOverlay.currentAt?.(address));
      const parts = [name, level, voltage, current, summarizeNet(net)].filter(Boolean); // prettier-ignore
      this.#netStatus.textContent = parts.join(" · ");
      this.#netStatus.classList.toggle("net-status--named", Boolean(name));
      if (level) this.#netStatus.dataset.level = level;
      else delete this.#netStatus.dataset.level;
      this.#netStatus.classList.toggle("net-status--pinned", pinned);
      this.#netStatus.hidden = false;
    } else {
      this.#netStatus.hidden = true;
    }
  }

  /**
   * Right-click while probing: name / rename / clear the net under the cursor
   * — a hole, a wire, or (over nothing) the pinned net. Returns true when it
   * showed a menu (the caller then suppresses the default desk menu).
   */
  onContextMenu(world, e) {
    if (!this.#armed) return false;
    const hit = this.#hitTest(world);
    // A hole under the cursor wins; failing that the wire under the cursor
    // stands in for its own net (its `from` end), and only then the pin.
    const onWire = this.#hoverWire ? this.#doc.getWire(this.#hoverWire) : null;
    const address = hit?.address ?? onWire?.from ?? this.#anchor;
    const netId = address ? this.#netlist.netOf(address) : null;
    if (!netId) return false;
    const current = this.#netlist.nameOf(netId);
    const items = [
      {
        label: current ? t("probe.renameNet") : t("probe.nameNet"),
        onSelect: () => this.#promptName(address, netId, current),
      },
    ];
    if (current) {
      items.push({
        label: t("probe.clearName"),
        onSelect: () => this.#clearName(netId),
      });
    }
    if (this.#onAddToScope) {
      items.push({ separator: true });
      items.push({
        label: t("probe.addToAnalyzer"),
        onSelect: () => this.#onAddToScope(address),
      });
    }
    // The analyzer's black lead: clipped here, every voltage lane reads
    // against this net (and a floating AC adapter circuit is grounded here).
    if (this.#onSetScopeGround) {
      const lead = this.#doc.scopeGround;
      const here = lead != null && this.#netlist.netOf(lead) === netId;
      items.push(
        here
          ? { label: t("probe.removeAnalyzerGround"), onSelect: () => this.#onSetScopeGround(null) } // prettier-ignore
          : { label: t("probe.analyzerGround"), onSelect: () => this.#onSetScopeGround(address) }, // prettier-ignore
      );
    }
    PopupManager.menu({ x: e.clientX, y: e.clientY, items });
    return true;
  }

  /** The bound addresses that currently resolve to a net (0, 1, or a merge). */
  #bindingsOnNet(netId) {
    return this.#doc.netNames
      .filter((n) => this.#netlist.netOf(n.address) === netId)
      .map((n) => n.address);
  }

  #promptName(address, netId, current) {
    PopupManager.prompt({
      title: current ? t("probe.renameNetTitle") : t("probe.nameNetTitle"),
      label: t("probe.nameLabel"),
      value: current ?? "",
      placeholder: t("probe.namePlaceholder"),
      quickPicks: RESERVED_NET_NAMES,
      onConfirm: (name) => {
        if (!name) return;
        // Replace any OTHER bindings on this net so a rename never becomes a
        // self-conflict; the name binds to the point the user pointed at.
        const stale = this.#bindingsOnNet(netId).filter((a) => a !== address);
        this.#onNameNet?.(address, name, stale);
      },
    });
  }

  #clearName(netId) {
    const addresses = this.#bindingsOnNet(netId);
    if (addresses.length) this.#onClearNetNames?.(addresses);
  }
}
